import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatFormField } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatOptionModule } from '@angular/material/core';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FormsModule } from '@angular/forms';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatIconModule } from '@angular/material/icon';
import { BehaviorSubject, ReplaySubject } from 'rxjs';
import { OrganizationService } from '../../../services/organizations.service';
import { Logger } from '../../../../../infrastructure/logging/logger';
import { getColor, ColorProp } from '../../../../../layout/Colors';
import { AccountService } from '../../../../../shared/account/account.service';
import { SearchService } from '../../../../../shared/search/search.service';
import { SortParams, SortOrderType } from '../../../../../shared/search/SearchParams';
import { OrgMemberActivateConfirmDialogComponent } from './components/org-member-activate-confirm-dialog/org-member-activate-confirm-dialog.component';
import { OrgMembersInviteDialogComponent } from './components/org-members-invite-dialog/org-members-invite-dialog.component';
import { OrgMembersUpdateDialogComponent } from './components/org-members-update-dialog/org-members-update-dialog.component';
import { PillComponent } from '../../../../../shared/components/pill/pill.component';
import { OrganizationMemberColumns } from '../../../models/member/OrganizationMember';

interface MemberData {
  id: any;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: string;
  status: string;
}

@Component({
  selector: 'app-org-members',
  templateUrl: './org-members.component.html',
  styleUrls: ['./org-members.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatFormField,
    MatSelectModule,
    MatOptionModule,
    MatTableModule,
    MatInputModule,
    MatSortModule,
    MatIconModule,
    FormsModule,
    PillComponent,
    MatProgressSpinner,
    MatPaginator,
    MatButtonModule
  ]
})
export class OrgMembersComponent implements OnInit, AfterViewInit {

  @Input() org: any;
  @Output() organizationUpdated = new EventEmitter<any>();
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort: MatSort = new MatSort;

  selectedMember$ = new ReplaySubject<MemberData | null>();
  selectedMember: any | null = null;
  selectedMemberActive: boolean = false;
  selectedMemberId: any;

  loading = new BehaviorSubject<boolean>(false);
  displayedColumns = OrganizationMemberColumns;
  displayedColumnNames: string[] = [];
  dataSource = new MatTableDataSource<MemberData>();
  mappedMembers: any[] = [];
  keyword!: string;
  pageNumber: number = 1;
  tableLength = 0;
  pageSize = 10;
  activeFilter: boolean = false;
  inactiveFilter: boolean = false;

  constructor(
    private dialog: MatDialog,
    private accountService: AccountService,
    private organizationService: OrganizationService,
    private searchService: SearchService,
    private logger: Logger,
    private matSnackbar: MatSnackBar
  ) { }

  ngOnInit(): void {
    this.resetSearchQuery();
    this.initTableColumns();
    this.subscribeToSelectedMemberChanges();
    this.loadMembers();
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  initTableColumns(): void {
    if (this.displayedColumns === null) {
      this.displayedColumns = [];
    }
    this.displayedColumnNames = this.displayedColumns.map(c => c.column);
  }

  resetSearchQuery() {
    this.searchService.resetQuery();
    this.searchService.setSort("memberId", -1);
    this.searchService.setOrganizationId(this.org.id);
  }

  async loadMembers(clearSelectedMember: boolean = true) {
    try {
      this.loading.next(true);
      this.dataSource.data = [];
      if (clearSelectedMember) {
        this.clearSelectedMember();
      }

      this.mappedMembers = this.org.members.map((m: any) => this.buildMemberTableRow(m));
      this.updateTableDataSource(this.mappedMembers, this.org.members.length);
    } catch (e) {
      this.logger.handleError(e, 'An error occurred while retrieving Members', 'Error while retrieving Members');
    } finally {
      this.loading.next(false);
    }
  }

  onFilterActionClick(event: any) {
    this.onStatusFilterChange(event.value)
  }

  onSortEvent(event: any) {
    const sortParams = this.buildSortParams(event);
    if (sortParams.sortField === 'name') {
      sortParams.sortField = 'firstName';
    }

    this.searchService.addSort(sortParams);
    this.loadMembers(false);
  }

  private buildSortParams(event: any): SortParams {
    const { active: sortField, direction } = event;

    let sortOrder: SortOrderType;
    if (direction === 'asc') {
      sortOrder = 1;
    } else {
      sortOrder = -1;
    }

    return {
      sortField,
      sortOrder
    }
  }

  private buildMemberTableRow(member: any): any {
    let statusText = "Inactive";
    if (member.status_id === 1) {
      statusText = "Active";
    }
    return {
      id: member.account_id,
      firstName: member.first_name,
      lastName: member.last_name,
      email: member.email_address,
      phone: member.phone_number,
      role: member.roles[0].name,
      pillStatus: statusText
    };
  }

  private updateTableDataSource(members: MemberData[], totalRecords: number) {
    this.dataSource.data = members;
    this.tableLength = totalRecords;
  }

  private subscribeToSelectedMemberChanges(): void {
    this.selectedMember$.subscribe(member => {
      if (!member) {
        this.selectedMemberId = null;
        this.selectedMember = null;
        return;
      }
      this.selectedMember = member;
      this.setMemberActiveStatus(this.selectedMember);
    });
  }

  setMemberActiveStatus(member: any | null) {
    if (!member) {
      this.selectedMemberActive = false;
      return;
    }
    this.selectedMemberActive = member.pillStatus == 'Active';
  }

  updateSelectedMember(): void {
    const selectedMember = this.dataSource.data.find((member: MemberData) => member.id == this.selectedMemberId);
    this.selectedMember$.next(selectedMember || null);
  }

  clearSelectedMember() {
    this.selectedMember$.next(null);
  }

  onInviteMemberClick(): void {
    const dialogRef = this.dialog.open(OrgMembersInviteDialogComponent, {
      width: '60%',
      data: {
        firstName: '',
        lastName: '',
        emailAddress: '',
        phoneNumber: '',
        role: '',
        activationLink: true,
        organizationId: this.org.id
      },
      disableClose: true
    });
    dialogRef.afterClosed().subscribe(async data => {
      let updatePayload = {
        accountId: data.accountId,
        firstName: data.firstName,
        lastName: data.lastName,
        dateOfBirth: data.dateOfBirth,
        phoneNumber: data.phoneNumber,
        emailAddress: data.emailAddress,
      }
      await this.accountService.updateAccountInfo(updatePayload);
      this.organizationUpdated.emit();
    });
  }

  async onMemberClick(member: any): Promise<void> {
    const memberId = member.id;
    if (this.selectedMemberId && this.selectedMemberId == memberId) {
      this.selectedMemberId = '';
      this.selectedMember$.next(null);
    } else {
      this.selectedMemberId = memberId;
      this.updateSelectedMember();
    }
  }

  async onSetMemberStatusClick(): Promise<void> {
    const dialogRef = this.dialog.open(OrgMemberActivateConfirmDialogComponent, {
      width: '50%',
      data: { 
        activating: !this.selectedMemberActive,
        action: 'Status'
      },
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(async confirmed => {
      if (!confirmed) {
        return;
      }

      await this.accountService.updateMemberAccountStatus(this.selectedMemberId, this.selectedMemberActive ? 2 : 1);
      this.organizationUpdated.emit();
    });
  }

  async onEditMemberClick(): Promise<void> {
    try {
      const dialogRef = this.dialog.open(OrgMembersUpdateDialogComponent, {
        width: '55%',
        data: {
          accountId: this.selectedMemberId,
          firstName: this.selectedMember?.firstName,
          middleName: '',
          lastName: this.selectedMember?.lastName,
          emailAddress: this.selectedMember?.email,
          phoneNumber: this.selectedMember.phone
        }
      });
      dialogRef.afterClosed().subscribe(shouldLoadPage => {
        if (shouldLoadPage) {
          this.organizationUpdated.emit();
        }
      });
    } catch (e) {
      this.logger.handleError(e, 'An error occurred while updating Member', `Error while updating Member for memberId: ${this.selectedMemberId}`);
    }
  }

  onUpdateMemberRoleClick() {
    const dialogRef = this.dialog.open(OrgMemberActivateConfirmDialogComponent, {
      width: '50%',
      data: { 
        role: this.selectedMember?.role,
        action: 'Role'
      },
      disableClose: true
    });
    dialogRef.afterClosed().subscribe(async confirmed => {
      if (!confirmed) {
        return;
      } else {
        let newRoleId;
        if (this.selectedMember?.role === 'Org Admin') {
          newRoleId = 3;
        } else {
          newRoleId = 2;
        }

        this.accountService.updateMemberAccountRole(this.selectedMember?.id, newRoleId).then(_ => {
          this.organizationUpdated.emit();
        });
      }
    });
  }

  onStatusFilterChange(event: any): void {
    let status = event.value;
    if (status == 'all') {
      this.removeStatusFilter();
    } else {
      this.updateStatusFilter(this.getFormattedStatusValue(status));
    }
  }

  removeStatusFilter() {
    this.searchService.removeFilter('status');
    this.dataSource.data = this.mappedMembers;
  }

  getFormattedStatusValue(status: string): string {
    switch(status) {
      case 'active':
        return 'Active';
      case 'inactive':
        return 'Inactive';
      default:
        this.logger.handleError(`Unknown status value: ${status}`, `Unknown status value: ${status}`, `Unknown status value: ${status}`);
        return '';
    }
  }

  updateStatusFilter(status: string) {
    this.dataSource.data = this.mappedMembers;
    switch(status) {
      case "Active":
        this.dataSource.data = this.mappedMembers.filter(member => member.pillStatus === 'Active');
        break;
      case "Inactive":
        this.dataSource.data = this.mappedMembers.filter(member => member.pillStatus === 'Inactive');
        this.tableLength = this.dataSource.data.length;
        break;
      default:
        this.dataSource.data = this.mappedMembers;
        break;
    }
  }

  getMemberStatusColor(status: string): string {
    if (status?.toLowerCase() === 'active') {
      return getColor(ColorProp.PropertyStatusActive);
    } else {
      return '#d3d3d3';
    }
  }

  onPageEvent(event: PageEvent): void {
    this.pageNumber = event.pageIndex + 1;
  }

  applySearch(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  clearSearch() {
    this.keyword = '';
    this.dataSource = new MatTableDataSource(this.mappedMembers);
    this.ngAfterViewInit();
  }

  onGoToPageClick() {
    const max = Math.trunc(Math.ceil(this.paginator.length / this.paginator.pageSize));
    if (this.pageNumber < 1) {
      this.resetPaginator(1, true);
      this.displayInvalidPaginatorPageIndexError();
    } else if (this.pageNumber > max) {
      this.resetPaginator(max, false);
      this.displayInvalidPaginatorPageIndexError();
    } else {
      this.paginator.pageIndex = this.pageNumber - 1;
      this.paginator.page.next({
        pageIndex: this.pageNumber - 1,
        pageSize: this.paginator.pageSize,
        length: this.paginator.length
      });
    }
  }

  resetPaginator(pageNumber: number, goToFirstPage: boolean) {
    this.pageNumber = pageNumber;

    if (this.paginator) {
      if (goToFirstPage) {
        this.paginator.firstPage();
      } else {
        this.paginator.lastPage();
      }
    }
  }

  displayInvalidPaginatorPageIndexError() {
    this.matSnackbar.open('Invalid page number', 'OK', { duration: 3000 });
  }
}